<template>
  <PageContainer>
    <View gap="lg" direction="column">
      <View gap="md" justify="space-between">
        <Text variant="h1">Upcoming showings</Text>
        <ScrollableHorizontallyControls :scrollable-container="scrollableContainerRef" />
      </View>

      <View v-if="state.status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="state.status === 'error'"
        title="Could not load showings"
        description="Please reload the page or try again later"
      />

      <template v-else>
        <EmptyPagePlaceholder
          v-if="groupedByDateShowings.length === 0"
          title="You don't have any showings yet"
          description="Tallo is working to schedule showings. Once there is activity, you can see everything related to showings here"
        />

        <ScrollableHorizontallyContainer v-else ref="scrollableContainerRef">
          <View gap="lg" padding-top="md" class="showings">
            <View
              gap="md"
              direction="column"
              v-for="showingsOnDate in groupedByDateShowings"
              :key="showingsOnDate.date.getTime()"
            >
              <View gap="sm">
                <Text variant="h4">{{ useDateFormat(showingsOnDate.date, 'MMM D') }}</Text>
                <Text variant="body-large" color="tertiary">
                  {{ useDateFormat(showingsOnDate.date, 'dddd') }}
                </Text>
              </View>

              <View gap="md" direction="column">
                <ShowingCard
                  class="showing"
                  v-for="showing in showingsOnDate.showings"
                  :key="showing.id"
                  :showing="showing"
                  :show-status="false"
                />
              </View>
            </View>
          </View>
        </ScrollableHorizontallyContainer>
      </template>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';

import {
  PageContainer,
  Text,
  View,
  EmptyPagePlaceholder,
  ScrollableHorizontallyContainer,
  ScrollableHorizontallyControls,
  LoadingIndicator,
} from '@tallo/design-system';
import { groupShowingByDateAndSort, ShowingCard, ShowingStatus, useShowingsQuery } from '@tallo/investor/showing';

// Only the confirmed tours, filtered by the server: the whole history (1,800+ at rise24 to show 30)
// took 2–4 s to load
const { state } = useShowingsQuery({ status: ShowingStatus.CONFIRMED });
const scrollableContainerRef = ref();

const groupedByDateShowings = computed(() => groupShowingByDateAndSort(state.value.data || []));
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.showings {
  @include mobile {
    flex-direction: column;
    gap: var(--t-spacing-lg);
  }

  .showing {
    min-width: 23rem;

    @include mobile {
      min-width: 100%;
    }
  }
}
</style>
